<template>
  <header class="w-full">
    <div
     
      class="fixed inset-x-0 top-0 z-10 flex h-16 shrink-0 border-b border-gray-200 bg-white shadow-sm lg:relative"
    >
      <nuxt-link
       
        to="/home"
        class="flex size-16 shrink-0 items-center justify-center bg-indigo-500 p-3 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 lg:hidden"
        @click="resetSelectedRollout"
      >
        <img
          class="h-full"
          src="~/assets/img/icon-white.svg"
          alt="Rollouter Logo"
        />
      </nuxt-link>
      <div class="flex flex-1 justify-between px-4 sm:px-6">
        <div class="flex flex-1 items-center lg:block">
          <LayoutSearchBar v-if="isStaff" class="h-full flex-auto" />
          <LayoutNotificationsMenu
            v-if="isNotificationMenuVisible"
            class="lg:hidden"
          />
        </div>
        <div class="hidden items-center lg:flex">
          <LayoutRolloutSelection class="ml-6" />
        </div>
        <div
          class="ml-4 hidden items-center space-x-4 sm:ml-6 sm:space-x-6 lg:flex"
        >
          <LayoutNotificationsMenu
            v-if="isNotificationMenuVisible"
            class="hidden lg:block"
          />

          <!-- Profile dropdown -->
          <Menu as="div" class="relative shrink-0">
            <div>
              <MenuButton
                class="flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span class="sr-only">Open user menu</span>
                <UserAvatar :has-popover="false" :user="user" size="8" />
              </MenuButton>
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <MenuItems
                class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
              >
                <MenuItem v-slot="{ active }">
                  <span
                    :class="[
                      active ? 'bg-gray-100' : '',
                      'block cursor-pointer px-4 py-2 text-sm text-gray-700',
                    ]"
                    @click="useRouter().push('/account')"
                  >
                    {{ $t('general.your-account') }}
                  </span>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <span
                    :class="[
                      active ? 'bg-gray-100' : '',
                      'block cursor-pointer text-sm text-gray-700',
                    ]"
                    @click="darkModeEnabled = !darkModeEnabled"
                  >
                    <Toggle
                      :model-value="darkModeEnabled"
                      :label="$t('general.dark-mode')"
                      size="small"
                      label-position="left"
                      class="w-full justify-between px-4 py-2"
                    />
                  </span>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <span
                    :class="[
                      active ? 'bg-gray-100' : '',
                      'block cursor-pointer px-4 py-2 text-sm text-gray-700',
                    ]"
                    @click="signOut"
                  >
                    {{ $t('general.sign-out') }}
                  </span>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>

          <button
            v-if="canAccessActionsSlideOver"
            type="button"
            class="flex items-center justify-center rounded-full bg-indigo-600 p-1 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            @click="showActionsSlideOver = true"
          >
            <PlusIcon class="size-6" aria-hidden="true" />
            <span class="sr-only">Actions</span>
          </button>
        </div>
      </div>

      <!-- Menu button area -->
      <div class="inset-y-0 flex items-center pr-4 sm:pr-6 lg:hidden">
        <!-- Mobile menu button -->
        <button
          type="button"
          class="-mr-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600"
         
          @click="mobileMenuOpen = true"
        >
          <span class="sr-only">Open main menu</span>
          <Bars3Icon class="block size-6" aria-hidden="true" />
        </button>
      </div>
    </div>

    <LayoutActionsSlideOver v-model="showActionsSlideOver" />
  </header>
</template>

<script lang="ts" setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { Bars3Icon, PlusIcon } from '@heroicons/vue/24/outline';
// @ts-expect-error no types package available
import nightwind from 'nightwind/helper';
import { UserRole } from '@/config/user-roles';
import { useUserStore } from '@/stores/user';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const mobileMenuOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

// User

const userStore = useUserStore();
const user = computed(() => userStore.user);

const signOut = async () => {
  await userStore.signOut();
};

const isStaff = computed(
  () => user.value && user.value.role >= UserRole.SERVICE_DESK
);

// Rollout Selection

const { selectedRollout } = useRolloutRouting();

const resetSelectedRollout = () => {
  selectedRollout.value = false;
};

// Actions slide over

const canAccessActionsSlideOver = computed(
  () => user.value && user.value.role >= UserRole.SERVICE_DESK
);

const showActionsSlideOver = ref(false);

// Dark mode

const { saveUserSettings } = useUserSettings();

const darkModeEnabled = computed({
  get: () => (user.value && user.value.settings?.enableDarkMode) ?? false,
  set: async (enableDarkMode) => {
    await saveUserSettings({ enableDarkMode }, () => {
      nightwind.enable(userStore.user?.settings?.enableDarkMode);
    });
  },
});

// Notifications

const isNotificationMenuVisible = computed(
  () => userStore.user && userStore.user.role > UserRole.CLIENT
);
</script>
